import React, { createContext, useState, useEffect, useContext } from 'react';
import { fetchData } from '../utils/apiClient';

interface PermissionsContextType {
  permissions: string[];
  loading: boolean;
  error: string | null;
  refreshPermissions: () => Promise<void>;
  hasPermission: (perm: string) => boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchData('/users/permissions', 'GET_USER_PERMISSIONS');
      setPermissions(response.permissions || []);
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setError('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions().then(()=>{
      console.log('permission fetched')
    });
  }, []);

  const hasPermission = (perm: string): boolean => {
    return permissions.includes(perm.trim());
  };

  return (
    <PermissionsContext.Provider value={{ permissions, loading, error, refreshPermissions: fetchPermissions, hasPermission }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export function usePermissions(permission?: string): any {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  if (permission) {
    return context.hasPermission(permission);
  }
  return context;
}
